import { useEffect, useState } from "react";
import { ClipboardList } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { alert } from "@/contexts/AlertModalContext";
import type { CategoriaItemResponse } from "@/types/categoria-item-response";

/**
 * Valores do passo "Informação individual em massa". Tudo em texto, como vem
 * dos inputs: validade em "AAAA-MM-DD" (valor do <input type="date">).
 * Campo vazio = não mexe no que o item já tem.
 */
export type InfoIndividualValores = {
  quantidade: string;
  valor: string;
  validade: string;
};

export const INFO_INDIVIDUAL_VAZIA: InfoIndividualValores = {
  quantidade: "",
  valor: "",
  validade: "",
};

/**
 * Converte a data guardada no item ("DD/MM/AAAA", "AAAA-MM-DD" ou ISO) para o
 * formato que o <input type="date"> aceita. Sem passar por Date, para não
 * cair no dia anterior por causa do fuso.
 */
export function dataParaInput(data: string | null | undefined): string {
  if (!data) return "";
  const texto = String(data).trim();
  const br = texto.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (br) return `${br[3]}-${br[2].padStart(2, "0")}-${br[1].padStart(2, "0")}`;
  const iso = texto.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return iso ? `${iso[1]}-${iso[2]}-${iso[3]}` : "";
}

/** "AAAA-MM-DD" -> "DD/MM/AAAA" (formato que as telas de EPC guardam no item). */
export function dataInputParaBr(data: string): string {
  const iso = data.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return iso ? `${iso[3]}/${iso[2]}/${iso[1]}` : "";
}

/** Informação individual padrão cadastrada na categoria (back office). */
export function valoresDaCategoria(
  categoria: CategoriaItemResponse | undefined | null,
): InfoIndividualValores {
  if (!categoria) return INFO_INDIVIDUAL_VAZIA;
  return {
    quantidade: categoria.quantidade != null ? String(categoria.quantidade) : "",
    valor: categoria.valor != null ? String(categoria.valor) : "",
    validade: dataParaInput(categoria.dt_validade),
  };
}

export function temAlgumValor(v: InfoIndividualValores): boolean {
  return !!(v.quantidade.trim() || v.valor.trim() || v.validade);
}

type CamposItem<T> = {
  quantidade: keyof T;
  valor: keyof T;
  validade: keyof T;
  /** Formato da validade no item: "br" = DD/MM/AAAA, "iso" = AAAA-MM-DD. */
  formatoData: "br" | "iso";
  /** Como o valor é guardado no item. */
  valorComo: "number" | "string";
};

/** Campos das telas que geram EPCs (EpcItemGerado). */
export const CAMPOS_EPC = {
  quantidade: "quantidade_bo",
  valor: "valor_bo",
  validade: "dt_validade_bo",
  formatoData: "br",
  valorComo: "number",
} as const;

/**
 * Aplica os valores a todos os itens. Devolve um array novo; campo vazio não
 * sobrescreve o valor do item. Se `soOndeVazio`, só preenche itens que ainda
 * não têm o campo (usado para puxar o padrão da categoria sem apagar edições).
 */
export function aplicarInfoIndividual<T>(
  itens: T[],
  valores: InfoIndividualValores,
  campos: CamposItem<T>,
  soOndeVazio = false,
): T[] {
  const quantidade = valores.quantidade.trim();
  const valorTexto = valores.valor.trim().replace(",", ".");
  const validade = valores.validade
    ? campos.formatoData === "br"
      ? dataInputParaBr(valores.validade)
      : valores.validade
    : "";

  const vazio = (v: unknown) => v === undefined || v === null || v === "" || v === 0;

  return itens.map((item) => {
    const novo = { ...item } as Record<string, unknown>;
    const pode = (campo: keyof T) => !soOndeVazio || vazio(novo[campo as string]);

    if (quantidade && pode(campos.quantidade)) {
      novo[campos.quantidade as string] = quantidade;
    }
    if (valorTexto && pode(campos.valor)) {
      novo[campos.valor as string] =
        campos.valorComo === "number" ? Number(valorTexto) : valorTexto;
    }
    if (validade && pode(campos.validade)) {
      novo[campos.validade as string] = validade;
    }
    return novo as T;
  });
}

/**
 * Mescla valores: o que estiver preenchido em `sobre` prevalece sobre `base`.
 * Usado para "valor do item, senão o padrão da categoria".
 */
export function mesclarInfoIndividual(
  base: InfoIndividualValores,
  sobre: InfoIndividualValores | undefined,
): InfoIndividualValores {
  if (!sobre) return base;
  return {
    quantidade: sobre.quantidade.trim() ? sobre.quantidade : base.quantidade,
    valor: sobre.valor.trim() ? sobre.valor : base.valor,
    validade: sobre.validade || base.validade,
  };
}

/** "AAAA-MM-DD" -> "DD/MM/AAAA" só para exibir no badge. */
function exibirData(data: string): string {
  return dataInputParaBr(data) || data;
}

type CellProps = {
  valores: InfoIndividualValores;
  onSalvar: (valores: InfoIndividualValores) => void;
};

/**
 * Célula "Informação individual" para telas cujo item não tem os campos
 * próprios (ex.: Ordens de Impressão, onde a linha é um array de texto).
 * Mesmo visual do popover das telas de impressão.
 */
export function InfoIndividualCell({ valores, onSalvar }: CellProps) {
  const [open, setOpen] = useState(false);
  const [rascunho, setRascunho] = useState<InfoIndividualValores>(valores);

  const { quantidade, valor, validade } = valores;
  useEffect(() => {
    if (!open) setRascunho({ quantidade, valor, validade });
  }, [open, quantidade, valor, validade]);

  const valorNum = Number(String(valor).replace(",", ".")) || 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant={temAlgumValor(valores) ? "outline" : "ghost"} size="sm">
          {temAlgumValor(valores) ? (
            <div className="flex flex-wrap gap-1">
              {quantidade && (
                <Badge variant="secondary" className="font-normal">
                  Qtd: {quantidade}
                </Badge>
              )}
              {valorNum > 0 && (
                <Badge variant="secondary" className="font-normal">
                  R$ {valorNum.toFixed(2)}
                </Badge>
              )}
              {validade && (
                <Badge variant="secondary" className="font-normal">
                  Val: {exibirData(validade)}
                </Badge>
              )}
            </div>
          ) : (
            <span className="text-muted-foreground">+ Atribuir</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72">
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label className="text-xs">Quantidade</Label>
            <Input
              autoComplete="off"
              type="number"
              min={0}
              value={rascunho.quantidade}
              onChange={(e) =>
                setRascunho((p) => ({ ...p, quantidade: e.target.value }))
              }
            />
          </div>
          <div className="grid gap-1.5">
            <Label className="text-xs">Valor (R$)</Label>
            <Input
              autoComplete="off"
              type="number"
              step="0.01"
              min={0}
              value={rascunho.valor}
              onChange={(e) => setRascunho((p) => ({ ...p, valor: e.target.value }))}
            />
          </div>
          <div className="grid gap-1.5">
            <Label className="text-xs">Validade</Label>
            <Input
              autoComplete="off"
              type="date"
              value={rascunho.validade}
              onChange={(e) =>
                setRascunho((p) => ({ ...p, validade: e.target.value }))
              }
            />
          </div>
          <Button
            size="sm"
            onClick={() => {
              onSalvar(rascunho);
              setOpen(false);
            }}
          >
            Salvar
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

type Props = {
  /** Número do passo exibido no círculo (mesmo visual dos outros passos). */
  numero?: number;
  totalItens: number;
  /** Sugestão inicial (ex.: valores da categoria). Recarrega quando muda. */
  valoresIniciais?: InfoIndividualValores;
  onAplicar: (valores: InfoIndividualValores) => void;
  disabled?: boolean;
};

/**
 * Passo "Informação individual em massa": define quantidade, valor e validade
 * de uma vez para todos os itens da ordem. Depois, cada item continua podendo
 * ser editado na coluna "Informação individual".
 */
export function InfoIndividualEmMassa({
  numero,
  totalItens,
  valoresIniciais = INFO_INDIVIDUAL_VAZIA,
  onAplicar,
  disabled,
}: Props) {
  const [valores, setValores] = useState<InfoIndividualValores>(valoresIniciais);

  const { quantidade: qI, valor: vI, validade: dI } = valoresIniciais;
  useEffect(() => {
    setValores({ quantidade: qI, valor: vI, validade: dI });
  }, [qI, vI, dI]);

  const alterar = (campo: keyof InfoIndividualValores, v: string) =>
    setValores((prev) => ({ ...prev, [campo]: v }));

  const aplicar = () => {
    const quantidade = valores.quantidade.trim();
    const valor = valores.valor.trim().replace(",", ".");
    if (quantidade && (!Number.isInteger(Number(quantidade)) || Number(quantidade) < 0)) {
      alert.error("Quantidade deve ser um número inteiro maior ou igual a zero.");
      return;
    }
    if (valor && (!Number.isFinite(Number(valor)) || Number(valor) < 0)) {
      alert.error("Valor deve ser um número maior ou igual a zero.");
      return;
    }
    if (!temAlgumValor(valores)) {
      alert.error("Preencha ao menos um campo para aplicar.");
      return;
    }
    onAplicar(valores);
    alert.success(
      `Informação individual aplicada a ${totalItens} ${totalItens === 1 ? "item" : "itens"}.`,
    );
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          {numero != null && (
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
              {numero}
            </div>
          )}
          <CardTitle className="text-sm font-medium flex items-center gap-1.5">
            <ClipboardList className="h-4 w-4 text-muted-foreground" />
            Informação individual em massa
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Define a informação individual de todos os itens da ordem de uma vez.
          Campo em branco não altera o que o item já tem; depois dá para ajustar
          item a item.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="grid gap-2">
            <Label htmlFor="info-massa-quantidade" className="text-xs text-muted-foreground">
              Quantidade
            </Label>
            <Input
              id="info-massa-quantidade"
              autoComplete="off"
              type="number"
              min={0}
              step={1}
              disabled={disabled}
              value={valores.quantidade}
              onChange={(e) => alterar("quantidade", e.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="info-massa-valor" className="text-xs text-muted-foreground">
              Valor (R$)
            </Label>
            <Input
              id="info-massa-valor"
              autoComplete="off"
              type="number"
              min={0}
              step="0.01"
              disabled={disabled}
              value={valores.valor}
              onChange={(e) => alterar("valor", e.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="info-massa-validade" className="text-xs text-muted-foreground">
              Validade
            </Label>
            <Input
              id="info-massa-validade"
              autoComplete="off"
              type="date"
              disabled={disabled}
              value={valores.validade}
              onChange={(e) => alterar("validade", e.target.value)}
            />
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            size="sm"
            disabled={disabled || totalItens === 0}
            onClick={aplicar}
          >
            Aplicar a todos os itens ({totalItens})
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
